import { useState, useRef, useEffect } from 'react';

interface Pays {
  code: string;
  nom: string;
  indicatif: string;
  drapeau: string;
  placeholder: string;
  maxLength: number;
}

const PAYS_LIST: Pays[] = [
  { code: 'BJ', nom: 'Bénin', indicatif: '+229', drapeau: '🇧🇯', placeholder: '01 91 12 12 61', maxLength: 10 },
  { code: 'FR', nom: 'France', indicatif: '+33', drapeau: '🇫🇷', placeholder: '6 12 34 56 78', maxLength: 11 },
  { code: 'US', nom: 'USA', indicatif: '+1', drapeau: '🇺🇸', placeholder: '202 555 0123', maxLength: 10 },
  { code: 'CA', nom: 'Canada', indicatif: '+1', drapeau: '🇨🇦', placeholder: '416 555 0123', maxLength: 10 },
  { code: 'CI', nom: "Côte d'Ivoire", indicatif: '+225', drapeau: '🇨🇮', placeholder: '07 08 09 10', maxLength: 10 },
  { code: 'SN', nom: 'Sénégal', indicatif: '+221', drapeau: '🇸🇳', placeholder: '77 123 45 67', maxLength: 10 },
  { code: 'ML', nom: 'Mali', indicatif: '+223', drapeau: '🇲🇱', placeholder: '76 12 34 56', maxLength: 9 },
  { code: 'BF', nom: 'Burkina Faso', indicatif: '+226', drapeau: '🇧🇫', placeholder: '70 12 34 56', maxLength: 9 },
  { code: 'TG', nom: 'Togo', indicatif: '+228', drapeau: '🇹🇬', placeholder: '90 12 34 56', maxLength: 9 },
  { code: 'NE', nom: 'Niger', indicatif: '+227', drapeau: '🇳🇪', placeholder: '90 12 34 56', maxLength: 9 },
  { code: 'CM', nom: 'Cameroun', indicatif: '+237', drapeau: '🇨🇲', placeholder: '6 12 34 56 78', maxLength: 10 },
  { code: 'GA', nom: 'Gabon', indicatif: '+241', drapeau: '🇬🇦', placeholder: '06 12 34 56', maxLength: 9 },
  { code: 'CG', nom: 'Congo', indicatif: '+242', drapeau: '🇨🇬', placeholder: '06 12 34 56', maxLength: 9 },
  { code: 'CD', nom: 'RDC', indicatif: '+243', drapeau: '🇨🇩', placeholder: '90 12 34 567', maxLength: 10 },
  { code: 'GH', nom: 'Ghana', indicatif: '+233', drapeau: '🇬🇭', placeholder: '24 123 4567', maxLength: 10 },
  { code: 'NG', nom: 'Nigeria', indicatif: '+234', drapeau: '🇳🇬', placeholder: '803 123 4567', maxLength: 11 },
  { code: 'BE', nom: 'Belgique', indicatif: '+32', drapeau: '🇧🇪', placeholder: '470 12 34 56', maxLength: 10 },
  { code: 'CH', nom: 'Suisse', indicatif: '+41', drapeau: '🇨🇭', placeholder: '79 123 45 67', maxLength: 10 },
  { code: 'GB', nom: 'Royaume-Uni', indicatif: '+44', drapeau: '🇬🇧', placeholder: '7911 123456', maxLength: 11 },
  { code: 'DE', nom: 'Allemagne', indicatif: '+49', drapeau: '🇩🇪', placeholder: '151 23456789', maxLength: 12 },
];

interface PhoneInputProps {
  value?: string;
  onChange: (phone: string) => void;
  label?: string;
  required?: boolean;
  id?: string;
  name?: string;
  disabled?: boolean;
}

export default function PhoneInput({
  value = '',
  onChange,
  label,
  required = false,
  id = 'phone',
  name = 'phone',
  disabled = false,
}: PhoneInputProps) {
  // Détecter le pays à partir de la valeur initiale
  const detecterPays = (phoneValue: string): Pays => {
    let clean = phoneValue.replace(/\s/g, '');
    if (!clean || clean === '') return PAYS_LIST[0]; // Bénin par défaut
    clean = clean.replace(/^\+/, '');

    // Chercher l'indicatif le plus long d'abord pour éviter les conflits (+1 vs +17)
    const sorted = [...PAYS_LIST].sort((a, b) => b.indicatif.length - a.indicatif.length);
    for (const pays of sorted) {
      if (clean.startsWith(pays.indicatif.replace('+', ''))) {
        return pays;
      }
    }
    return PAYS_LIST[0];
  };

  const paysInitial = detecterPays(value);
  const [paysSelectionne, setPaysSelectionne] = useState<Pays>(paysInitial);
  const [numeroLocal, setNumeroLocal] = useState('');
  const [dropdownOuvert, setDropdownOuvert] = useState(false);
  const [recherchePays, setRecherchePays] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Extraire le numéro local de la valeur initiale
  useEffect(() => {
    let clean = value.replace(/\s/g, '');
    if (clean) clean = clean.replace(/^\+/, '');
    const indicatif = paysSelectionne.indicatif.replace('+', '');
    if (clean && clean.startsWith(indicatif)) {
      let local = clean.substring(indicatif.length);
      // Pour le Bénin : le format national commence par 0
      // Si le local fait 9 chiffres (sans le 0), on le remet pour l'affichage
      if (paysSelectionne.code === 'BJ' && !local.startsWith('0') && local.length === 9) {
        local = '0' + local;
      }
      setNumeroLocal(formaterNumeroLocal(local, paysSelectionne));
    } else {
      setNumeroLocal('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOuvert(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formaterNumeroLocal = (input: string, pays: Pays): string => {
    const chiffres = input.replace(/\D/g, '');

    // Formatage selon le pays
    if (pays.code === 'BJ') {
      if (chiffres.length <= 2) return chiffres;
      if (chiffres.length <= 4) return `${chiffres.slice(0, 2)} ${chiffres.slice(2)}`;
      if (chiffres.length <= 6) return `${chiffres.slice(0, 2)} ${chiffres.slice(2, 4)} ${chiffres.slice(4)}`;
      if (chiffres.length <= 8) return `${chiffres.slice(0, 2)} ${chiffres.slice(2, 4)} ${chiffres.slice(4, 6)} ${chiffres.slice(6)}`;
      return `${chiffres.slice(0, 2)} ${chiffres.slice(2, 4)} ${chiffres.slice(4, 6)} ${chiffres.slice(6, 8)} ${chiffres.slice(8)}`;
    }

    if (pays.code === 'FR') {
      if (chiffres.length <= 1) return chiffres;
      if (chiffres.length <= 3) return `${chiffres.slice(0, 1)} ${chiffres.slice(1)}`;
      if (chiffres.length <= 5) return `${chiffres.slice(0, 1)} ${chiffres.slice(1, 3)} ${chiffres.slice(3)}`;
      if (chiffres.length <= 7) return `${chiffres.slice(0, 1)} ${chiffres.slice(1, 3)} ${chiffres.slice(3, 5)} ${chiffres.slice(5)}`;
      if (chiffres.length <= 9) return `${chiffres.slice(0, 1)} ${chiffres.slice(1, 3)} ${chiffres.slice(3, 5)} ${chiffres.slice(5, 7)} ${chiffres.slice(7)}`;
      return `${chiffres.slice(0, 1)} ${chiffres.slice(1, 3)} ${chiffres.slice(3, 5)} ${chiffres.slice(5, 7)} ${chiffres.slice(7, 9)} ${chiffres.slice(9)}`;
    }

    if (pays.code === 'US' || pays.code === 'CA') {
      if (chiffres.length <= 3) return chiffres;
      if (chiffres.length <= 6) return `${chiffres.slice(0, 3)} ${chiffres.slice(3)}`;
      return `${chiffres.slice(0, 3)} ${chiffres.slice(3, 6)} ${chiffres.slice(6)}`;
    }

    // Par défaut : groupement par 2
    const groupes: string[] = [];
    for (let i = 0; i < chiffres.length; i += 2) {
      groupes.push(chiffres.slice(i, i + 2));
    }
    return groupes.join(' ');
  };

  const handleNumeroChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const chiffres = raw.replace(/\D/g, '');

    if (chiffres.length > paysSelectionne.maxLength) return;

    const formate = formaterNumeroLocal(chiffres, paysSelectionne);
    setNumeroLocal(formate);

    // Construire le numéro complet international
    const indicatifSansPlus = paysSelectionne.indicatif.replace('+', '');

    // Pour le Bénin : le format local commence par 0 (ex: 01 91 12 12 61)
    // En format international, ce 0 disparaît : +229 1 91 12 12 61
    let chiffresPourComplet = chiffres;
    if (paysSelectionne.code === 'BJ' && chiffres.startsWith('0')) {
      chiffresPourComplet = chiffres.substring(1);
    }

    const complet = `+${indicatifSansPlus}${chiffresPourComplet}`;
    onChange(complet);
  };

  const handlePaysChange = (pays: Pays) => {
    setPaysSelectionne(pays);
    setDropdownOuvert(false);
    setRecherchePays('');

    // Reconstruire le numéro complet avec le nouvel indicatif
    const chiffres = numeroLocal.replace(/\D/g, '');
    let chiffresPourComplet = chiffres;
    if (pays.code === 'BJ' && chiffres.startsWith('0')) {
      chiffresPourComplet = chiffres.substring(1);
    }
    if (chiffresPourComplet) {
      const indicatifSansPlus = pays.indicatif.replace('+', '');
      const complet = `+${indicatifSansPlus}${chiffresPourComplet}`;
      onChange(complet);
    } else {
      onChange('');
    }

    // Focus sur l'input
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const paysFiltres = recherchePays
    ? PAYS_LIST.filter(
        (p) =>
          p.nom.toLowerCase().includes(recherchePays.toLowerCase()) ||
          p.indicatif.includes(recherchePays) ||
          p.code.toLowerCase().includes(recherchePays.toLowerCase())
      )
    : PAYS_LIST;

  return (
    <div>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-clinic-blue-dark mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="flex items-stretch">
        {/* Sélecteur de pays */}
        <div className="relative flex-shrink-0" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => !disabled && setDropdownOuvert(!dropdownOuvert)}
            disabled={disabled}
            className="h-full px-3 py-3 bg-background-100 border-2 border-r-0 border-clinic-blue/20 rounded-l-2xl hover:bg-background-200 transition-colors flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-lg leading-none">{paysSelectionne.drapeau}</span>
            <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">
              {paysSelectionne.indicatif}
            </span>
            <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${dropdownOuvert ? 'rotate-180' : ''}`}></i>
          </button>

          {/* Dropdown */}
          {dropdownOuvert && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-xl border border-background-200 z-50 overflow-hidden">
              {/* Barre de recherche */}
              <div className="p-2 border-b border-background-100">
                <div className="flex items-center gap-2 px-3 py-2 bg-background-50 rounded-lg">
                  <i className="ri-search-line text-foreground-400 text-sm"></i>
                  <input
                    type="text"
                    value={recherchePays}
                    onChange={(e) => setRecherchePays(e.target.value)}
                    placeholder="Rechercher un pays..."
                    className="w-full bg-transparent text-sm outline-none text-foreground-800 placeholder:text-foreground-400"
                    autoFocus
                  />
                  {recherchePays && (
                    <button
                      type="button"
                      onClick={() => setRecherchePays('')}
                      className="text-foreground-400 hover:text-foreground-600 cursor-pointer"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Liste des pays */}
              <div className="max-h-60 overflow-y-auto">
                {paysFiltres.length === 0 ? (
                  <div className="p-4 text-center text-sm text-foreground-500">
                    Aucun pays trouvé
                  </div>
                ) : (
                  paysFiltres.map((pays) => (
                    <button
                      key={pays.code}
                      type="button"
                      onClick={() => handlePaysChange(pays)}
                      className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-background-50 transition-colors text-left cursor-pointer ${
                        pays.code === paysSelectionne.code ? 'bg-primary-50/50' : ''
                      }`}
                    >
                      <span className="text-xl leading-none flex-shrink-0 w-7 text-center">{pays.drapeau}</span>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-foreground-800 block truncate">{pays.nom}</span>
                      </div>
                      <span className="text-sm text-foreground-500 flex-shrink-0">{pays.indicatif}</span>
                      {pays.code === paysSelectionne.code && (
                        <i className="ri-check-line text-primary-500 text-sm flex-shrink-0"></i>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Input numéro */}
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="tel"
          required={required}
          disabled={disabled}
          value={numeroLocal}
          onChange={handleNumeroChange}
          placeholder={paysSelectionne.placeholder}
          className="flex-1 px-4 py-3 border-2 border-l-0 border-clinic-blue/20 rounded-r-2xl focus:outline-none focus:border-clinic-blue text-sm bg-white text-foreground-800 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>

      {/* Info bulle */}
      <p className="text-xs text-gray-500 mt-2 flex items-start gap-1">
        <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
        <span>
          Sélectionnez votre pays puis saisissez votre numéro local. Le système ajoute automatiquement l'indicatif {paysSelectionne.indicatif}.
        </span>
      </p>
    </div>
  );
}